import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { DownloadButton } from "../../components/Core/Button/Button";
import Dropdown from "../../components/Core/Dropdown/Dropdown";
import Flex from "../../components/Layout/Flex";
import Breadcrumbs from "../../components/Shared/Breadcrumbs/Breadcrumbs";
import { CandidatesReportBreadcrumbs } from "../../configs/breadcrumbNavigation";
import { RecruitmentActionType as action } from "../../configs/constants/RecruitmentActionType";
import { buildPoolKeyValues, statusTypes } from "../../configs/Reports.config";
import CertificationReportFilter, {
	CertificationFilterValue,
} from "../../components/Shared/CertificationReportFilter/CertificationReportFilter";
import { KeyValue } from "../../models/KeyValue";
import { useOrganisation } from "../../networking/organisation.hooks";
import { apiStore } from "../../stores/api.store";
import { reportStore } from "../../networking/report.api";

const CandidatesReportPage: React.FC = () => {
	const { t } = useTranslation();

	const { data: organisation } = useOrganisation();

	const [pool, setPool] = useState<KeyValue>();
	const [status, setStatus] = useState<KeyValue>();
	const [certification, setCertification] = useState<CertificationFilterValue>({});
	const [currentAction, setCurrentAction] = useState<KeyValue>();
	const [loading, setLoading] = useState<boolean>();

	const pools = buildPoolKeyValues(organisation?.pools || [], t("ALL"));

	const handlePoolChange = (e: KeyValue) => {
		setPool(e);
	};

	const handleStatusChange = (e: KeyValue) => {
		setStatus(e);
	};

	const handleCurrentActionChange = (e: KeyValue) => {
		setCurrentAction(e);
	};

	const onSubmit = async () => {
		let selectedPool!: string;
		let selectedStatus!: string;
		let selectedCurrentAction!: string;

		if (pool?.key && pool.key !== "") selectedPool = pool.key as string;

		if (status?.key && status.key !== "") selectedStatus = status.key as string;

		if (currentAction?.key && currentAction.key !== "")
			selectedCurrentAction = currentAction.key as string;

		setLoading(true);
		const url = await reportStore.getCandidatesReport(
			selectedPool,
			selectedStatus,
			selectedCurrentAction,
			certification
		);

		if (url) {
			// https://stackoverflow.com/a/59352848
			const downloadLink = document.createElement("a");
			downloadLink.href = apiStore.apiRoot + url;
			downloadLink.download = apiStore.apiRoot + url;
			downloadLink.click();
		}

		setLoading(false);
	};

	return (
		<div className="p-4 px-6">
			<Breadcrumbs items={CandidatesReportBreadcrumbs} />
			<Flex flexWrap="wrap" maxWidth="900px">
				<Dropdown<KeyValue, KeyValue>
					value={status}
					getValue={(o) => o}
					onChange={handleStatusChange}
					getOptionLabel={(option) => option.value}
					options={statusTypes()}
					placeholder={"reports.filters.status.placeHolder"}
					label={"reports.filters.status.label"}
					width="300px"
					margin="0.5rem"
				/>
				{pools.length > 0 && (
					<Dropdown<KeyValue, KeyValue>
						value={pool}
						getValue={(o) => o}
						onChange={handlePoolChange}
						getOptionLabel={(option) => option.value}
						options={pools}
						placeholder={"reports.filters.pool.placeHolder"}
						label={"reports.filters.pool.label"}
						width="300px"
						margin="0.5rem"
					/>
				)}
				<Dropdown<KeyValue, KeyValue>
					value={currentAction}
					getValue={(o) => o}
					onChange={handleCurrentActionChange}
					getOptionLabel={(option) => option.value}
					options={[
						{ key: "", value: t("ALL") },
						...Object.keys(action).map((key) => ({
							key: key,
							value: t(key as any),
						})),
					]}
					placeholder={"reports.filters.currentAction.placeHolder"}
					label={"reports.filters.currentAction.label"}
					width="300px"
					margin="0.5rem"
				/>
				<CertificationReportFilter
					certificationTypes={organisation?.certificationTypes}
					value={certification}
					onChange={setCertification}
					allLabel={t("ALL")}
					margin="0.5rem"
				/>
			</Flex>
			<DownloadButton
				width="200px"
				height="38px"
				fontSize="m"
				margin="1.5rem 0.5rem"
				onClick={onSubmit}
				loading={loading}
			>
				{t("reports.export")}
			</DownloadButton>
		</div>
	);
};

export default CandidatesReportPage;
